/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the component tree and displays a fallback UI
 */

import React, { Component, ErrorInfo, ReactNode } from 'react'
import { logError } from '../utils/errorHandler'

interface ErrorBoundaryProps {
	children: ReactNode
	fallback?: ReactNode
	onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface ErrorBoundaryState {
	hasError: boolean
	error: Error | null
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props)
		this.state = {
			hasError: false,
			error: null,
		}
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		// Update state so the next render will show the fallback UI
		return {
			hasError: true,
			error,
		}
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		// Log the error
		logError(error, 'ErrorBoundary')
		console.error('Error Info:', errorInfo)

		// Call custom error handler if provided
		if (this.props.onError) {
			this.props.onError(error, errorInfo)
		}
	}

	handleReset = (): void => {
		this.setState({
			hasError: false,
			error: null,
		})
	}

	render(): ReactNode {
		if (this.state.hasError) {
			// Custom fallback UI if provided
			if (this.props.fallback) {
				return this.props.fallback
			}

			// Default fallback UI
			return (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						minHeight: '400px',
						padding: '2rem',
						textAlign: 'center',
					}}
				>
					<h2 style={{ color: '#dc3545', marginBottom: '1rem' }}>Κάτι πήγε στραβά</h2>
					<p style={{ color: '#6c757d', marginBottom: '1.5rem' }}>Παρουσιάστηκε ένα απροσδόκητο σφάλμα. Παρακαλώ δοκιμάστε να ανανεώσετε τη σελίδα.</p>

					{process.env.NODE_ENV === 'development' && this.state.error && (
						<div
							style={{
								marginTop: '1rem',
								padding: '1rem',
								backgroundColor: '#f8d7da',
								border: '1px solid #f5c6cb',
								borderRadius: '4px',
								maxWidth: '600px',
								textAlign: 'left',
								fontSize: '0.875rem',
								color: '#721c24',
							}}
						>
							<strong>Error Details (Development Only):</strong>
							<pre style={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{this.state.error.toString()}</pre>
						</div>
					)}

					<div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
						<button
							onClick={this.handleReset}
							style={{
								padding: '0.5rem 1rem',
								backgroundColor: '#007bff',
								color: 'white',
								border: 'none',
								borderRadius: '4px',
								cursor: 'pointer',
							}}
						>
							Δοκιμάστε ξανά
						</button>

						<button
							onClick={() => window.location.reload()}
							style={{
								padding: '0.5rem 1rem',
								backgroundColor: '#6c757d',
								color: 'white',
								border: 'none',
								borderRadius: '4px',
								cursor: 'pointer',
							}}
						>
							Ανανέωση σελίδας
						</button>
					</div>
				</div>
			)
		}

		return this.props.children
	}
}

export default ErrorBoundary





